// CultivarDetails.js

import { useState, useEffect, useRef, useContext } from "react";
import { useFetch } from "use-http";
import { Form, Box, Text, Button, Layer, Header, Image, Spinner, Select, TextInput, grommet, Grommet } from "grommet";
import { getDBURL, updateStateHandler, generateSyncId, correctTimeZone, usePopupFocus } from "../Utils/Utils";
import useTrapFocus from "../Utils/useTrapFocus";
import { UserContext } from "../../App";
import { useDataContext } from '../Contexts/DataContext';
import { deepMerge } from "grommet/utils";
import Notification from "../Base components/Notification";
import Notes from "../Base components/Notes";
import VineCharacteristics from  "../Base components/VineCharacteristics";
import WarningPopupWindow from "../Base components/WarningPopupWindow";
import { useErrorLogger } from '../Base components/Logger';

const customTheme = deepMerge(grommet, {
    global: {
        colors: {
            brand: 'neutral-3',
            text: "neutral-3",
            border: "neutral-3",
            selected: "light-4",
        }, 
  
    },
    textInput: {
        extend: ({ hoverIndicator }) => {
            if (hoverIndicator) {            
                return `
                    &:hover {
                        cursor: ${"pointer"};
                    }
                    `;
            }
        }
    },  
    radio: {
        extend: ({ hoverIndicator }) => {
            if (hoverIndicator) {            
                return `
                    &:hover {
                        background-color: yellow;
                        transform: 'scale(1.8)';
                    }
                    `;
            }
        }
    }, 
    radioButton: {
        hover: {
            border: {
                color: '#6FFFB0',
           },
        },
        border: {
            width: '2px',
            color: "brand",
        },
        size: '16px', // affects the size of the outer circle
    }        
});

const CultivarDetails = (props) => {
    const { cultivar, createFromStation } = props;
    console.log('CultivarDetails props  >>  ', props);

    const currentUser = useContext(UserContext);
    const { DBlogError } = useErrorLogger();
    const { rootstockList } = useDataContext();
    // console.log('rootstockList  >>  ', rootstockList);
    const [loading, setLoading] = useState(true);    
    const { get, post } = useFetch(getDBURL());

    const [success, setSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");

    const [cultivarData, setCultivarData] = useState(null);
    const [originalCultivarData, setOriginalCultivarData] = useState(null); // Store initial state
    const [isButtonClicked, setIsButtonClicked] = useState(false);

    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');

    const closeWarning = () => {
        setShowWarning(false);
        setWarningMessage('');
    }

     // Ref for the modal and focus trap to keep focus within it  
     const modalRef = useRef(null);
     useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open
 
    // Manage focus when opening modal windows
    usePopupFocus(showWarning, "warning-msg");   

    useEffect(() => {
        // console.log("In useEffect, fired on first mounting !! ");
        (async function loadCultivarData() {
            // console.log('In loadCultivarData cultivar data >> ', cultivar);
            // console.log('In loadCultivarData cultivar createFromStation >> ', createFromStation);
            
            try {
                if( cultivar) {
                    const loadedData = {
                        CultivarID: cultivar.CultivarID,  
                        CultivarName: cultivar.CultivarName === null ? '' : cultivar.CultivarName,     
                        Brand: cultivar.Brand === null ? '' : cultivar.Brand,
                        Mother: cultivar.Mother === null ? '' : cultivar.Mother,
                        Father: cultivar.Father === null ? '' : cultivar.Father,
                        // RootstockId: cultivar.RootstockId,
                        RootstockName: cultivar.Rootstock,
                        Type: cultivar.Type, 
                        Harvest: cultivar.Harvest, 
                        BudBreak: cultivar.BudBreak, 
                        RainResistance: cultivar.RainResistance, 
                        Chilling: cultivar.Chilling,
                        CreateDate: cultivar.CreateDate,
                        CreatedBy: cultivar.CreatedBy,                        
                        IsCommercial: cultivar.IsCommercial,
                        CommercialFromDate: cultivar.CommercialFromDate,
                        SetAsCommercialBy: cultivar.SetAsCommercialBy,
                        AddDate: cultivar.AddDate,
                        AddedBy: cultivar.AddedBy,
                        Country: cultivar.Country,
                        Company: cultivar.Company,
                        Notes: cultivar.Notes,            
                        LastSyncDate: cultivar.LastSyncDate,
                    };
                    setCultivarData(loadedData);
                    setOriginalCultivarData(JSON.parse(JSON.stringify(loadedData))); // Deep copy for comparison
                }
                setLoading(false);                
            }
            catch (error) {
                console.error('in loadCultivarData error: ' + error.message);
                setShowError(true);
                setNotificationMessage(error.message); 

                // Construct error object
                const errorDetails = {
                    error: error,
                    parameters: JSON.stringify(cultivar),
                    userName: currentUser.email,
                    moduleName: 'CultivarDetails',
                    functionName: 'loadCultivarData',
                }
                await DBlogError(errorDetails);
            }
        } ) ()        
    }, [cultivar]);  

    const onCultivarNameChange = (newName) => {
        if (newName !== "") {        
            updateStateHandler( setCultivarData, 'CultivarName', newName);
        }
    }; 

    const onBrandChange = (newValue) => {        
        updateStateHandler( setCultivarData, 'Brand', newValue);
    };

    const onMotherChange = (newValue) => {        
        updateStateHandler( setCultivarData, 'Mother', newValue);
    };
    
    const onFatherChange = (newValue) => {        
        updateStateHandler( setCultivarData, 'Father', newValue);
    };

    const onRootstChange = ( option ) => {
        // console.log('option >> ', option);
        if (option === undefined) {
            updateStateHandler( setCultivarData, 'RootstockId', 0);
            updateStateHandler( setCultivarData, 'RootstockName', '');            
        }
        else {
            updateStateHandler( setCultivarData, 'RootstockId', option.Id);
            updateStateHandler( setCultivarData, 'RootstockName', option.Name);
        }
    };

    const hasChanges = () => {
        const isCultivarEqual = JSON.stringify(cultivarData) === JSON.stringify(originalCultivarData);
        return !(isCultivarEqual);
    };

    const submitHandler = async (event) => {
        event.preventDefault(); // Prevent default form submission behavior

        if (cultivarData.CultivarName === '') 
        {
            setWarningMessage("Cultivar creation failed: A name is required. Please provide a valid name before proceeding.");
            setShowWarning(true);
            return;
        } 

        // Special handling station update to make it commercial.
        const isToCommercialUpdate = createFromStation && cultivarData.CultivarID !== null;

        if (!hasChanges() && !isToCommercialUpdate) {
            // Optionally show a toast saying "No changes to save"
            props.onClosePopup?.();
            return;
        }
    
        // Get the updated LastSyncDate
        const lastSyncDate = correctTimeZone(new Date());
        // console.log('createFromStation >>>> !!!!', createFromStation);

        // Update 'LastSyncDate' in cultivarData without relying on async setState
        let updatedCultivarData = { ...cultivarData, LastSyncDate: lastSyncDate };
    
        const timestamp = Date.now();
        let result = null;
        let userMessage = "";
        try {
            // Check if inserting a new cultivar or updating an existing one
            if (updatedCultivarData.CultivarID === null) {
                // Prepare data for a new insert
                updatedCultivarData = {
                    ...updatedCultivarData,
                    Id: generateSyncId(),
                    AddDate: correctTimeZone(new Date()),
                    AddedBy: currentUser.email,
                };    
                // console.log('Inserting new cultivar:', updatedCultivarData);
    
                // Insert new cultivar
                const response = await post("/insertCultivar", updatedCultivarData);
    
                if (!response.success) {
                    throw new Error(`Insert failed! ${response.message}`);
                }
    
                result = response.result; // Assume the result contains Id of the new cultivar
                userMessage = `New cultivar '${updatedCultivarData.CultivarName}' added successfully!`;

            } else {
                if(createFromStation)
                {
                    updatedCultivarData = {
                        ...updatedCultivarData,
                        CommercialFromDate: correctTimeZone(new Date()),
                        SetAsCommercialBy: currentUser.email,
                    };
                    userMessage = `The station '${updatedCultivarData.CultivarName}' was successfully marked as commercial.`
                }
    
                // Update existing cultivar
                const response = await post(`/updateCultivar/dt=${timestamp}`, updatedCultivarData);
    
                if (!response.success) {
                    throw new Error(`Update failed! ${response.message}`);
                }
    
                result = response.result; // Assume the result contains 1
                if(!createFromStation)
                    userMessage = `The cultivar '${updatedCultivarData.CultivarName}' was successfully updated.`;
            }
    
            if (result) {
                // After successful insert/update, trigger callbacks
                setIsButtonClicked(true);
                if (props.loadCultivarsList)
                    await props.loadCultivarsList();
                setSuccess(true);

                // Set the notification message
                setNotificationMessage(userMessage);

                // Delay closing the popup to give the user time to read the message
                setTimeout(() => {
                    props.onClosePopup?.();
                }, 2000); // Delay of 2000 milliseconds = 2 second
            }
    
        } catch (error) {
            console.error('Error during submitHandler:', error.message);
            setShowError(true);
            // Set a user-friendly message
            setNotificationMessage("Something went wrong while saving the cultivar. Please try again  later.");

            // Construct error object
            const errorDetails = {
                error: error,
                parameters: JSON.stringify(updatedCultivarData),
                userName: currentUser.email,
                moduleName: 'CultivarDetails',
                functionName: 'submitHandler',
            }
            await DBlogError(errorDetails);
        }
    };

    return (

        <Grommet theme={customTheme} > 
        <Layer
            id="alertmsg"
            position="center"
            modal
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dim background for modal effect
            }}
            onEsc={props.onClosePopup}
            onClickOutside={props.onClosePopup}
        >
            <Box 
                overflow='auto'
                pad="small" 
                gap="small" 
                width="large"  
                background="light-3"  
                border={{ color: "brand"}}
                style={{
                    position: 'relative',
                    margin: 'auto', // Center horizontally and vertically
                }}
            >                 
                <Form 
                    id="cult-det"
                    ref={modalRef}
                    tabIndex="-1"
                    direction="row" 
                    justify="between" 
                    align="start" > 
                    {showError && (
                        <Notification
                        status="critical"
                        message={notificationMessage}
                        onClose={() => setShowError(false)}
                    />
                    )} 

                    {success && (
                        <Notification
                        status="normal"
                        message={notificationMessage}
                        onClose={() => setSuccess(false)}
                    /> 
                    )}   

                    {loading ? (
                    <Box align="center" direction="row" gap="small" pad="small">
                        <Spinner /> <Text>Loading...</Text>
                    </Box>
                    ) : ( 

                    <>
                        {showWarning && (
                            <WarningPopupWindow msg={warningMessage} close={closeWarning}/>
                        )}

                        <Box  margin="auto" width="large" 
                            background="light-3" border>
                            <Box fill
                                pad="small" gap="medium"
                                background={{
                                    image: "url('/Magnifier.jpg')",  // Adjusting transparency of the gradient layer
                                    size: "contain",
                                    repeat: "no-repeat",
                                    opacity: 0.2,  // Opacity for the combined image and gradient
                                        }} >
                                <Box style={{position:"relative"}} >
                                    <Header  margin="none" background="light-4" pad="small" height="xxsmall" justify="around" >
                                        <Box  direction="row" align="end">
                                            <Box direction="row" >
                                                <Text style={{whiteSpace: 'pre-line', fontWeight: "bold", fontSize: "26px"
                                                            , color:'#FFAA15', padding: "0 5px", border: "none"
                                                        }}   >
                                                    Cultivar Information
                                                </Text>
                                            </Box>                                          
                                        </Box> 

                                    </Header>
                                    <Box height="xsmall" width="xsmall" 
                                        style={{position:"absolute", right: "3%", top: "15px"}}
                                        right="0px"
                                        >
                                            <Image
                                                size="contain"
                                                src="Grape.png"                                  
                                            />
                                    </Box>                 
                                </Box>

                                <Box direction="row-responsive"  alignSelf="center"  justify="around"  width="60%" >
                                    {/* Brand Input */}
                                    <Box direction="row" width="200px" align="center" >
                                            <Text size="medium"><strong>Brand:&nbsp;&nbsp;&nbsp;</strong></Text>
                                            <TextInput 
                                                value={cultivarData.Brand === null ? '' : cultivarData.Brand}
                                                placeholder="Enter Brand.."
                                                style={{
                                                    fontWeight: "bold", fontSize: "20px", width: "160px",
                                                    color: '#FFAA15', background: "#F2F2F2", border: "none",
                                                    paddingTop: "8px", height: "26px"
                                                }}
                                                onChange={(event) => {onBrandChange(event.target.value);}}
                                            />
                                    </Box>

                                    {/* Cultivar Name Input */}
                                    <Box align="center" width= "160px" margin={{ vertical: "small" }} style={{ position: 'relative', display: 'inline-block' }}>
                                        <TextInput
                                            value={cultivarData.CultivarName === null ? '' : cultivarData.CultivarName}
                                            placeholder="Enter Name..."
                                            onChange={(event) => {
                                                onCultivarNameChange(event.target.value);
                                            }}
                                            style={{
                                                fontWeight: "bold",
                                                fontSize: "xlarge",
                                                color: "#00739D", // Adjusted brand color
                                                textAlign: "center",
                                                padding: "5px",
                                                background: "none", // No background for TextInput itself
                                                fontStyle: "italic",
                                                border: "none", // Removes default input borders
                                                outline: "none", // Removes focus outline
                                                width: cultivarData.CultivarName ? `${cultivarData.CultivarName.length + 3}ch` : '140px', // Conditional width
                                            }}
                                        />
                                        <Box
                                            style={{
                                                height: '2px',
                                                width: cultivarData.CultivarName ? `${cultivarData.CultivarName.length + 5}ch` : '140px', // Conditional width
                                                background: "linear-gradient(to left, #00739D, #f9dd94)",
                                                position: 'absolute',
                                                bottom: '0',
                                                left: '0',
                                            }}
                                        />
                                    </Box>


                                </Box>



                                {/* <Box direction="row-responsive" alignSelf="center" width="98%" >

                                        {/* Rootstock Select *
                                        <Box direction="row" width="50%" align="center"
                                        
                                        >
                                            <Text size="medium"><strong>Rootstock:&nbsp;&nbsp;</strong></Text>
                                            {cultivarData.CreateDate !== null ? (
                                                <Text size="medium" color='#FFAA15'>
                                                <strong>{cultivarData.RootstockName === null ? '' : cultivarData.RootstockName}</strong>
                                                </Text>
                                            ) : (
                                                <Select
                                                value={cultivarData.RootstockId === null ? 0 : cultivarData.RootstockId}
                                                valueKey={{key: "Id", reduce: true }} 
                                                labelKey="Name"
                                                placeholder="Select Rootstock..."
                                                style={{padding: "1px 0 1px 10px", margin: "0", width: "200px"}}
                                                options={rootstockList} 
                                                onChange={({option}) => onRootstChange(option)}                        
                                                emptySearchMessage={"No Rootstock Available"} 
                                                clear                                  
                                                />
                                            )}
                                        </Box>
                                        <Box direction="row" width="50%" align="center">
                                    </Box>
                                </Box> */}

                                <Box direction="row-responsive" alignSelf="center" width="94%" >
                                    {/* Mother Input */}
                                    <Box direction="row" width="50%" align="center">
                                        <Text size="medium"><strong>Mother:&nbsp;&nbsp;</strong></Text>
                                        {cultivarData.CreateDate !== null ? (
                                            <Text size="medium" color='#FFAA15' width= "200px">
                                            <strong>{cultivarData.Mother === null ? '' : cultivarData.Mother}</strong>
                                            </Text>
                                            ) : (
                                        <TextInput
                                            value={cultivarData.Mother === null ? '' : cultivarData.Mother}
                                            style={{
                                                fontWeight: "bold", fontSize: "20px", width: "200px",
                                                color: '#FFAA15', background: "#F2F2F2", border: "none",
                                                paddingTop: "8px", height: "26px"
                                            }}
                                            onChange={(event) => {onMotherChange(event.target.value);}}
                                        />
                                        )}
                                    </Box>

                                    {/* Father Input */}
                                    <Box direction="row" width="50%" align="center" >
                                        <Text size="medium"><strong>Father:&nbsp;&nbsp;</strong></Text>
                                        {cultivarData.CreateDate !== null ? (
                                            <Text size="medium" color='#FFAA15' width= "200px">
                                            <strong>{cultivarData.Father === null ? '' : cultivarData.Father}</strong>
                                            </Text>
                                            ) : (                                    
                                        <TextInput
                                            value={cultivarData.Father === null ? '' : cultivarData.Father}
                                            style={{
                                                fontWeight: "bold", fontSize: "20px", width: "200px",
                                                color: '#FFAA15', background: "#F2F2F2", border: "none",
                                                paddingTop: "8px", height: "26px"
                                            }}
                                            onChange={(event) => {onFatherChange(event.target.value);}}
                                        />
                                        )}
                                    </Box>

                                </Box>

                                <VineCharacteristics cultivarData={cultivarData} setCultivarData={setCultivarData}  readOnly={false}/>

                                <Box direction="row-responsive" alignSelf="center" width="94%" >
                                    {/* Company Input */}
                                    <Box direction="row" width="50%" align="center">
                                        <Text size="medium"><strong>Company:&nbsp;&nbsp;&nbsp;</strong></Text>
                                        <TextInput
                                            value={cultivarData.Company === null ? '' : cultivarData.Company}
                                            style={{
                                                fontWeight: "bold", fontSize: "20px", width: "200px",
                                                color: '#FFAA15', background: "#F2F2F2", border: "none",
                                                paddingTop: "8px", height: "26px"
                                            }}
                                            onChange={(event) => {updateStateHandler( setCultivarData, 'Company', event.target.value);}}
                                        />
                                    </Box>

                                    {/* Country Input */}
                                    <Box direction="row" width="50%" align="center">
                                        <Text size="medium"><strong>Country:&nbsp;</strong></Text>
                                        <TextInput
                                            value={cultivarData.Country === null ? '' : cultivarData.Country}
                                            style={{
                                                fontWeight: "bold", fontSize: "20px", width: "200px",
                                                color: '#FFAA15', background: "#F2F2F2", border: "none",
                                                paddingTop: "8px", height: "26px"
                                            }}
                                            onChange={(event) => {updateStateHandler( setCultivarData, 'Country', event.target.value);}}
                                        />
                                    </Box>

                                </Box>

                                <Box width="90%" height="100px" alignSelf="center">
                                    <Notes
                                        stateHandler={setCultivarData}
                                        initValue={cultivarData.Notes}
                                    //    disable={props.disable}
                                        fieldName="Notes"
                                    ></Notes>
                                </Box>    

                                <Box 
                                    as="footer"
                                    gap="small"
                                    direction="row"
                                    align="center"
                                    justify="around"
                                    pad={{ top: 'xxsmall', bottom: 'medium' }}
                                >
                                    <Box align="center" direction="row" gap="small" pad="small" justify="around">
                                        <Button primary label="Save" onClick={submitHandler} style={{paddingTop: '2px', paddingBottom: '2px', fontSize: '16px', color: 'white'}} disabled={isButtonClicked} />
                                        <Button secondary label="Cancel" onClick={props.onClosePopup} style={{ paddingTop: '2px', paddingBottom: '2px', fontSize: '16px'}} />
                                    </Box>
                                </Box>                                
                            </Box>
                        </Box>
                    </>)}
                </Form> 
            </Box>   
        </Layer> 
        </Grommet>
    );


    

}

export default CultivarDetails;